/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import {
  IMediaCategoryModel,
  getMediaTagsByType,
  MediaType,
  TimeHelper,
} from "@nf/common";
import React from "react";
import { useTranslation } from "next-i18next";
import cx from "classnames";

import styles from "./MediaAdditionalInfo.module.scss";

export interface MediaAdditionalInfoProps {
  type?: string;
  seasons?: number;
  episodes?: number;
  year?: number;
  duration?: number;
  categories?: IMediaCategoryModel[];
  className?: string;
}

export const MediaAdditionalInfo = ({
  type,
  seasons,
  episodes,
  year,
  duration,
  categories,
  className,
}: MediaAdditionalInfoProps) => {
  const { t } = useTranslation("translations");
  const metadata: string[] = [];

  if (type === MediaType.Collection) {
    return (
      <div className={cx(styles.metadata, className)}>
        {t("web_collection")}
      </div>
    );
  }

  if (categories?.length) {
    const mainCategories = getMediaTagsByType(categories);
    const mainCategory = mainCategories?.[0];

    if (mainCategory) {
      metadata.push(mainCategory.CategoryName);
    }
  }

  if (seasons) {
    const translationKey =
      seasons === 1 ? "web_season" : "web_asset_page_seasons";

    metadata.push(`${seasons} ${t(translationKey)}`);
  }

  if (episodes) {
    metadata.push(t("web_series_episode", { count: episodes }));
  }

  if (year) {
    metadata.push(`${year}`);
  }

  if (duration) {
    metadata.push(`${TimeHelper.formatDuration(duration)}`);
  }

  if (!metadata.length) {
    return null;
  }

  return (
    <div className={cx(styles.metadata, className)}>
      {metadata.map((data, index) => {
        const isLast = index === metadata.length - 1;

        return (
          <React.Fragment key={index}>
            <span>{data}</span>
            {!isLast && <span className={styles.separator} />}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default MediaAdditionalInfo;
